import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import { Button, mergeClasses } from '@fluentui/react-components';
import { ChevronLeft12Regular, ChevronRight12Regular } from '@fluentui/react-icons';

import { getFullyShownContentsCount } from './utils';
import { useStyles, HINT_WIDTH } from './styles';

export const Carousel = ({
  contents,
  contentWidth,
  i18n,
}) => {
  const [offset, setOffset] = useState(0);
  const classes = useStyles();
  const [contentContainerWidth, setContentContainerWidth] = useState(0);
  const contentContainerNode = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (contentContainerNode.current) {
        setContentContainerWidth(contentContainerNode.current.offsetWidth);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const measuredRef = useCallback((node) => {
    if (node) {
      contentContainerNode.current = node;
      setContentContainerWidth(contentContainerNode.current.offsetWidth);
    }
  }, []);

  const displayedContentCount = getFullyShownContentsCount(contentContainerWidth, contentWidth);
  const noMoreResult = offset + displayedContentCount + 1 > contents.length;

  const leftIconButton = useMemo(() => (
    <Button
      shape="circular"
      className={mergeClasses('paddle-left', classes.chevronButton, classes.chevronButtonLeft)}
      icon={<ChevronLeft12Regular />}
      onClick={() => setOffset(offset - 1)}
      onKeyDown={(event) => {
        if ((event.code === 'Enter' || event.code === 'Space') && offset <= 1) {
          document.querySelector('.paddle-right').focus();
          setOffset(offset - 1);
          event.preventDefault();
        }
      }}
      aria-label={i18n.getString(_TL_('click to see previous'))}
    />
  ), [i18n, offset, classes]);

  const rightIconButton = useMemo(() => (
    <Button
      shape="circular"
      icon={<ChevronRight12Regular />}
      className={mergeClasses('paddle-right', classes.chevronButton, classes.chevronButtonRight)}
      onClick={() => {
        const newOffset = offset + 1;
        if (newOffset + displayedContentCount <= contents.length) {
          setOffset(offset + 1);
        }
      }}
      onKeyDown={(event) => {
        if ((event.code === 'Enter' || event.code === 'Space') && offset + displayedContentCount >= contents.length - 1) {
          document.querySelector('.paddle-left').focus();
          setOffset(offset + 1);
          event.preventDefault();
        }
      }}
      aria-label={i18n.getString(_TL_('click to see next'))}
    />
  ), [displayedContentCount, i18n, contents, offset, classes]);

  const parentContainer = useMemo(() => {
    const left = offset > 0 ? ((-1 * contentWidth * offset) + HINT_WIDTH) : 0;
    return (
      <div
        className={classes.parentContainer}
        style={{ width: contentWidth * contents.length, left }}
        role="listbox"
        id="listbox"
        aria-orientation="horizontal"
        aria-label={i18n.getString(_TL_('Carousel contents'))}
      >
        {displayedContentCount > 0 &&
          <div
            role="contentinfo"
            aria-live="polite"
            aria-atomic="true"
            className={mergeClasses('liveregion', 'visuallyhidden')}
            aria-label={displayedContentCount === 1 ?
              i18n.getString(_TL_('Item {{current}} of {{total}}'), { current: offset + 1, total: contents.length })
              : i18n.getString(_TL_('Items {{current1}} and {{current2}} of {{total}}'), { current1: [...Array(displayedContentCount - 1)].map((item, index) => index + offset + 1).join(), current2: offset + displayedContentCount, total: contents.length })
            }
          />
        }
        {
          contents.map((content, index) => (
            <div
              key={`carousel-content-${index + 1}`}
              className="content"
              role="option"
              aria-selected={index === offset}
              id={`listbox-${index + 1}`}
              tabIndex={-1}
            >
              {content}
            </div>
          ))
        }
      </div>
    );
  }, [classes.parentContainer, contentWidth, contents, offset, i18n, displayedContentCount]);

  if (contents.length === 0) {
    return null;
  }

  const showRightIconButton = contents.length > displayedContentCount && !noMoreResult;

  return (
    <div className={mergeClasses('carousel-root', classes.carouselParent)}>
      {offset > 0 && leftIconButton}
      <div
        className={classes.container}
        ref={measuredRef}
        style={contentContainerWidth > 0 ? { width: contentContainerWidth } : {}}
      >
        {displayedContentCount > 0 && parentContainer}
      </div>
      {
        offset > 0 &&
        <div className={classes.hintLeft} />
      }
      {showRightIconButton &&
        <React.Fragment>
          <div className={classes.hintRight} />
          {rightIconButton}
        </React.Fragment>
      }
    </div>
  );
};

Carousel.propTypes = {
  contents: PropTypes.arrayOf(PropTypes.node).isRequired,
  contentWidth: PropTypes.number.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
};
